import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of } from 'rxjs';
import { Meta, Title } from '@angular/platform-browser';
import { API_CONFIG } from '../config/api.config';

export interface WebAppConfig {
  whatsapp: string;
  privacy_policy_url: string;
  short_description: string;
  copyright: string;
  contact_phone: string;
  facebook_link: string;
  description: string;
  url: string;
  terms_conditions_url: string;
  url_logo: string;
  support_email: string;
  instagram_link: string;
  contact_address: string;
  en_mantenimiento?: boolean;
}

export interface WebAppConfigResponse {
  success: boolean;
  message: string;
  data: {
    sk: string;
    configurationType: string;
    data: WebAppConfig;
    version: string;
    isActive: boolean;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebAppConfigService {
  private meta = inject(Meta);
  private titleService = inject(Title);

  // Signal para la configuración
  private configSignal = signal<WebAppConfig | null>(null);
  
  // Exponer como readonly
  config = this.configSignal.asReadonly();
  
  // Flag para saber si ya se cargó
  private loaded = false;

  constructor(private http: HttpClient) {}

  loadConfig(): Observable<WebAppConfigResponse | null> {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.webapp.configurations}`;
    
    return this.http.get<WebAppConfigResponse>(
      url,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        }
      }
    ).pipe(
      tap(response => {
        console.log('📡 Respuesta completa de configuración web:', response);
        
        // Manejar respuesta con estructura estándar {success, data, message}
        if ((response as any).success && (response as any).data) {
          const configData = (response as any).data.data;
          console.log('✅ Configuración cargada:', configData);
          console.log('🔧 en_mantenimiento:', configData.en_mantenimiento, 'tipo:', typeof configData.en_mantenimiento);
          this.configSignal.set(configData);
          this.loaded = true;
        } 
        // Manejar respuesta directa con estructura {sk, data}
        else if ((response as any).sk && (response as any).data) {
          const configData = (response as any).data;
          console.log('✅ Configuración cargada (formato alternativo):', configData);
          console.log('🔧 en_mantenimiento:', configData.en_mantenimiento, 'tipo:', typeof configData.en_mantenimiento);
          this.configSignal.set(configData);
          this.loaded = true;
        }
      }),
      catchError(error => {
        console.error('Error cargando configuración webapp:', error.message || error);
        return of(null);
      })
    );
  }

  applyHomeMetadata(): void {
    const cfg = this.config();
    const envSeo = API_CONFIG.seo;

    const siteName = envSeo?.siteName || 'Sitio';
    const title = envSeo?.title || (cfg?.short_description ? `${siteName} - ${cfg.short_description}` : `${siteName} - Compra tickets para eventos en Ecuador`);
    const description = envSeo?.description || cfg?.description || 'Compra tus entradas para eventos, conciertos y espectaculos en Ecuador de forma segura y rapida.';
    const keywords = envSeo?.keywords || `tickets, eventos, Ecuador, entradas, conciertos${cfg?.short_description ? `, ${cfg.short_description}` : ''}`;
    const url = envSeo?.url || cfg?.url || (typeof window !== 'undefined' ? `${window.location.origin}/` : '');
    const image = envSeo?.image || API_CONFIG.logoUrl || cfg?.url_logo || 'https://ticketexito-public.s3.us-east-1.amazonaws.com/images/logo/default_social_logo.jpg';
    const ogType = envSeo?.ogType || 'website';
    const twitterCard = envSeo?.twitterCard || 'summary_large_image';

    this.titleService.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords', content: keywords });

    this.meta.updateTag({ property: 'og:type', content: ogType });
    this.meta.updateTag({ property: 'og:site_name', content: siteName });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:url', content: url });

    this.meta.updateTag({ name: 'twitter:card', content: twitterCard });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    if (typeof document !== 'undefined' && image) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = image;
      }
    }
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  // Helpers para acceso rápido a propiedades comunes
  getLogoUrl(): string {
    return API_CONFIG.logoUrl || this.config()?.url_logo || '';
  }

  getSiteName(): string {
    return API_CONFIG.seo?.siteName || 'Sitio';
  }

  getSupportEmail(): string {
    return this.config()?.support_email || '';
  }

  getWhatsapp(): string {
    return this.config()?.whatsapp || '';
  }

  getCopyright(): string {
    return this.config()?.copyright || `© 2026 ${this.getSiteName()}`;
  }

  getSocialLinks(): { facebook: string; instagram: string } {
    return {
      facebook: this.config()?.facebook_link || '',
      instagram: this.config()?.instagram_link || ''
    };
  }

  getFooterInfo(): { facebookUrl: string; instagramUrl: string; twitterUrl: string; phone: string; waPhone: string; soporteEmail: string; copyrightText: string } {
    const env = API_CONFIG.footerInfo;
    const cfg = this.config();
    return {
      facebookUrl:   env?.facebookUrl   || cfg?.facebook_link  || '#',
      instagramUrl:  env?.instagramUrl  || cfg?.instagram_link || '#',
      twitterUrl:    env?.twitterUrl    || '#',
      phone:         env?.phone         || cfg?.whatsapp        || '',
      waPhone:       env?.waPhone       || cfg?.whatsapp?.replace('+', '') || '',
      soporteEmail:  env?.soporteEmail  || cfg?.support_email  || '',
      copyrightText: env?.copyrightText || cfg?.copyright       || `© ${this.getSiteName()}. Todos los derechos reservados`,
    };
  }

  /**
   * Forzar recarga de la configuración sin caché
   * Útil para actualizar configuración en tiempo real
   */
  reloadConfig(): Observable<WebAppConfigResponse | null> {
    console.log('🔄 Forzando recarga de configuración...');
    this.loaded = false;
    return this.loadConfig();
  }
}
